import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, CircularProgress, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { searchOnenote, getOnenotePage, getOnenotePageText } from '../../../utils/onenoteSearch';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import {
  gekoppeldeOneNoteIds, isOnbewerkt, oneNoteNotitie, oneNoteVoorstellen, zonderOneNoteBron,
} from '../../../utils/koppelVoorstellen';
import { bevestig } from '../../../utils/bevestig';
import { T, stitchSectionLabel } from '../parts/stitch';
import { useProjectData } from '../ProjectDataContext';

/**
 * OneNote-voorstellen in het koppelscherm (wiki 26 sep): pagina's uit het
 * team-notebook op projectnummer, klantnaam of projectnaam. Vinkje = de pagina
 * als projectnotitie bewaren (zelfde bronregel "onenote:<id>" als
 * /notes/onenote-import; de engine maakt er per project nooit twee van); vinkje
 * uit = ontkoppelen (onbewerkt: notitie weg; bewerkt: alleen de koppeling weg).
 * Met "Ongedaan maken" (Ctrl+Z). Bestaande koppelingen staan er altijd in.
 */
export default function OneNoteVoorstellen() {
  const { id, project, notes, notitieLokaalErbij, notitieLokaalWeg } = useProjectData();
  const nummer = String(project?.fields?.Projectnummer || '');
  const naam = project?.fields?.Projectnaam || '';
  const klantRaw = project?.fields?.KlantnaamString;
  const klant = Array.isArray(klantRaw) ? (klantRaw[0] || '') : (klantRaw || '');

  const [paginas, setPaginas] = useState(null); // null = zoekt
  const [fout, setFout] = useState('');
  const [bezig, setBezig] = useState(null);

  useEffect(() => {
    let actueel = true;
    (async () => {
      const termen = [nummer, klant, naam.slice(0, 40)].filter((t) => String(t).trim().length >= 3);
      if (!termen.length) { setPaginas([]); return; }
      const res = await Promise.allSettled(termen.map((t) => searchOnenote(t, { top: 8 })));
      if (!actueel) return;
      const mislukt = res.find((r) => r.status === 'rejected');
      if (mislukt && res.every((r) => r.status === 'rejected')) setFout(mislukt.reason?.message || 'OneNote niet bereikbaar');
      setPaginas(res.flatMap((r) => r.value || []));
    })();
    return () => { actueel = false; };
  }, [nummer, klant, naam]);

  const gekoppeld = useMemo(() => gekoppeldeOneNoteIds(notes), [notes]);
  const voorstellen = useMemo(
    () => oneNoteVoorstellen(paginas || [], { nummer, klant, naam }, gekoppeld),
    [paginas, nummer, klant, naam, gekoppeld],
  );

  // Eén notitie aanmaken (POST is idempotent per project + OneNote-pagina: bestaat
  // hij al, dan komt die terug met bestond_al). → { id, bestondAl }
  const plaats = useCallback(async ({ titel, inhoud, notitiedatum }) => {
    const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId: id, title: titel, notes: inhoud, source: 'OneNote', notitiedatum: notitiedatum || undefined }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    const d = await r.json();
    const record = d?.record || null;
    // Bronregel altijd in de lokale kopie, zodat het vinkje meteen klopt.
    if (record) {
      notitieLokaalErbij?.({ ...record, fields: { Onderwerp: titel, Notes: inhoud, ...(notitiedatum ? { Notitiedatum: notitiedatum } : {}), ...(record.fields || {}) } });
    }
    return { id: record?.id || null, bestondAl: Boolean(d?.bestond_al) };
  }, [id, notitieLokaalErbij]);

  const verwijder = useCallback(async (notitieId) => {
    const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${notitieId}?projectId=${id}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    notitieLokaalWeg?.(notitieId);
  }, [id, notitieLokaalWeg]);

  const wijzigTekst = useCallback(async (note, tekst) => {
    const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${note.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId: id, notes: tekst }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    notitieLokaalErbij?.({ ...note, fields: { ...(note.fields || {}), Notes: tekst } });
  }, [id, notitieLokaalErbij]);

  const koppel = async (p) => {
    const tekst = await getOnenotePageText(p.id);
    const { titel, inhoud } = oneNoteNotitie(p, tekst);
    const { id: notitieId, bestondAl } = await plaats({ titel, inhoud, notitiedatum: p.modified ? String(p.modified).slice(0, 10) : '' });
    if (bestondAl) { toast('Deze pagina stond al bij Notities', { icon: 'ℹ️' }); return; }
    biedOngedaanAan(`"${titel.slice(0, 50)}" gekoppeld als notitie`, async () => {
      if (notitieId) await verwijder(notitieId);
    });
  };

  // Ontkoppelen (Codex 26 sep): een onbewerkte import mag weg — ongedaan maken zet
  // exact dezelfde inhoud terug. Is de notitie aangepast (eigen aanvullingen, andere
  // titel), dan blijft hij staan en halen we na bevestiging alleen de koppeling
  // (de regel onenote:<id>) weg; ongedaan maken zet die regel terug in dezelfde notitie.
  const ontkoppel = async (p) => {
    const { note } = gekoppeld.get(p.id) || {};
    if (!note) return;
    // Altijd vergelijken met de échte pagina zoals hij nu in OneNote staat (titel, link
    // én tekst opnieuw ophalen) — niet met de rij in dit scherm: voor een gekoppelde
    // pagina buiten de zoekresultaten komt die titel uit de notitie zelf, en dan zou
    // een alleen-hernoemde notitie als 'onbewerkt' gelden (Codex-hercheck 26 sep).
    // Lukt ophalen niet → behandelen als bewerkt (bevestigen, alleen koppeling weg).
    let onbewerkt = false;
    try {
      const [echt, tekst] = await Promise.all([getOnenotePage(p.id), getOnenotePageText(p.id)]);
      onbewerkt = isOnbewerkt(note, oneNoteNotitie(echt, tekst));
    } catch { onbewerkt = false; }
    const f = note.fields || {};
    if (onbewerkt) {
      const kopie = { titel: f.Onderwerp || '', inhoud: f.Notes || '', notitiedatum: f.Notitiedatum || '' };
      await verwijder(note.id);
      biedOngedaanAan(`"${String(kopie.titel).slice(0, 50)}" ontkoppeld`, async () => { await plaats(kopie); });
      return;
    }
    const ok = await bevestig({
      titel: 'Notitie is aangepast',
      tekst: 'Deze notitie is na het koppelen bewerkt. Alleen de OneNote-koppeling weghalen? De notitie zelf blijft bij Notities staan.',
      knop: 'Koppeling weghalen',
    });
    if (!ok) return;
    const oud = f.Notes || '';
    await wijzigTekst(note, zonderOneNoteBron(oud, p.id));
    biedOngedaanAan('OneNote-koppeling weggehaald — de notitie blijft staan', async () => { await wijzigTekst(note, oud); });
  };

  const wissel = async (p) => {
    if (bezig) return;
    setBezig(p.id);
    try {
      if (p.gekoppeld) await ontkoppel(p);
      else await koppel(p);
    } catch (e) { toast.error(`OneNote ${p.gekoppeld ? 'ontkoppelen' : 'koppelen'} mislukt: ${e.message}`); }
    finally { setBezig(null); }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>
        OneNote{paginas ? ` (${voorstellen.length})` : ''}
      </Typography>
      {paginas === null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: T.outline }}>
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>Team-notebook doorzoeken…</Typography>
        </Box>
      )}
      {paginas !== null && fout && (
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>OneNote zoeken lukte niet: {fout}</Typography>
      )}
      {paginas !== null && !fout && voorstellen.length === 0 && (
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
          Geen pagina's gevonden op projectnummer, klant- of projectnaam.
        </Typography>
      )}
      {voorstellen.map((p) => (
        <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: `1px solid ${T.surfaceLow}` }}>
          <Checkbox size="small" checked={p.gekoppeld} disabled={bezig === p.id} onChange={() => wissel(p)}
            inputProps={{ 'aria-label': `${p.title} ${p.gekoppeld ? 'ontkoppelen' : 'koppelen'}` }} sx={{ p: 0.5 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.text }}>
              {p.webUrl
                ? <Box component="a" href={p.webUrl} target="_blank" rel="noreferrer" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{p.title}</Box>
                : p.title}
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
              {[p.modified ? String(p.modified).slice(0, 10) : '', p.gekoppeld ? 'staat bij Notities' : p.reden].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {bezig === p.id && <CircularProgress size={14} />}
        </Box>
      ))}
    </Box>
  );
}
